import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Row, Col, Statistic, Badge, Space } from 'antd';
import { PlayCircleOutlined, ArrowLeftOutlined, YoutubeFilled, CalendarOutlined, EyeOutlined, FileTextOutlined, BookFilled, CarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

// Import game images
import tooManySoccerBallsImg from '../../assets/games/too-many-soccer-balls.webp';
import oniPaniCastleImg from '../../assets/games/oni-pani-castle.webp';
import findTheChickensImg from '../../assets/games/find-the-chickens.webp';
import escapeChickenFactoryImg from '../../assets/games/escape-chicken-factory.webp';

const { Title, Paragraph, Text } = Typography;

const MetaversePage: React.FC = () => {
  const navigate = useNavigate();
  const [showBookingButton, setShowBookingButton] = useState(false);

  // Show booking button after animation duration (assuming 30 seconds for demo)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBookingButton(true);
    }, 20000); // 30 seconds

    return () => clearTimeout(timer);
  }, []);

  const featuredGame = {
    category: 'メタバース競技場での試合',
    title: 'Too many soccer balls',
    subtitle: 'サッカーボールバトル',
    releaseDate: '2025年6月リリース',
    visits: '460万',
    description: '伝説のサッカープレイヤーからサッカーボールを盗み、他のプレイヤーと白熱のバトル！隕石、竜巻、マグマ流などの予測不能な障害物が登場する、スリル満点のアクションゲーム',
    robloxLink: 'https://www.roblox.com/games/80764129972506/Brainrot-Too-many-soccer-balls',
    youtubeLink: 'https://www.youtube.com/watch?v=0qWLFRB6dJU',
    pressReleaseLink: 'https://prtimes.jp/main/html/rd/p/000000855.000000730.html',
    gradient: '#ffffff',
    imageStyle: {
      background: `url(${tooManySoccerBallsImg})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    },
    emoji: '⚽',
    isHot: true,
    features: [
      'サッカーボールをめぐる白熱のバトル',
      'ダイナミックな障害物（隕石、竜巻、マグマ流など）',
      '伝説のプレイヤーも参戦'
    ]
  };

  const otherGames = [
    {
      title: 'Oni Pani Castle',
      category: '観光・周遊促進​',
      releaseDate: '2025年8月リリース',
      description: '江戸時代風メタバースでボスを倒せ！ワンクリックで現実の江戸を周遊しよう',
      robloxLink: 'https://www.roblox.com/games/135405140096840/ESCAPE-ONI-PANI-OBBY-Alpha',
      youtubeLink: 'https://www.youtube.com/watch?v=pzbcYU1c6Dg',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      emoji: '👹',
      imageAlt: 'Oni Pani Castle - 江戸時代風世界',
      imagePath: oniPaniCastleImg,
      bookButtonText: 'スタジアムへ配車予約​',
      features: ['江戸時代風の世界観', 'SEKIRO風のステージデザイン', '妖怪城でのボスバトル']
    },
    {
      title: 'Find the chickens',
      category: '教育・アート',
      releaseDate: '2025年7月リリース',
      description: 'クリックから始まる、教育とアートの旅はここから。​',
      robloxLink: 'https://www.roblox.com/games/124324841468910/Find-the-Chickens',
      youtubeLink: 'https://youtu.be/fBMboAW1Bg8',
      gradient: 'linear-gradient(135deg, #feca57 0%, #ff9ff3 100%)',
      emoji: '🐔',
      imageAlt: 'Find the chickens - チキン探しゲーム',
      imagePath: findTheChickensImg,
      bookButtonText: 'アートギャラリー／教育イベントへ配車予約​',
      features: ['1000種類以上のチキン', '豊富なシーンとの相互作用', 'RNG要素']
    },
    {
      title: 'Escape the Chicken Factory',
      category: 'エンターテインメント​',
      releaseDate: '2024年11月リリース',
      description: '映画のように逃げろ！バーチャル体験からロケ地へ​',
      robloxLink: 'https://www.roblox.com/games/121158105689628/Escape-the-Chicken-Factory',
      youtubeLink: 'https://www.youtube.com/watch?v=6yFGiyMMqlw',
      gradient: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
      emoji: '🏭',
      imageAlt: 'Escape the Chicken Factory - チキン工場脱出ゲーム',
      imagePath: escapeChickenFactoryImg,
      bookButtonText: 'ロケ地・アトラクション​へ配車予約​​',
      features: ['Chicken Run風の世界観', '警備員・犬からの逃走', 'パイ製造機などの仕掛け']
    }
  ];

  const openLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(1);
              box-shadow: 0 4px 12px rgba(255,77,79,0.4);
            }
            50% {
              transform: scale(1.05);
              box-shadow: 0 6px 20px rgba(255,77,79,0.6);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 4px 12px rgba(255,77,79,0.4);
            }
          }
        `}
      </style>
      <div style={{ padding: '20px 0', minHeight: '100vh', background: '#f5f5f5' }}>
        <div className="container">


        {/* Page Header */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Title level={1} style={{ fontSize: 48, marginBottom: 16, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            🎮 AESオリジナルメタバースワールド​
          </Title>
          <Paragraph style={{ fontSize: 18, color: '#666', maxWidth: 600, margin: '0 auto' }}>
            公開したオリジナル作品
          </Paragraph>
        </div>

        {/* Featured Game - Too many soccer balls */}
        <Card
          style={{
            marginBottom: 40,
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}
        >
          <div
            style={{
              background: featuredGame.gradient,
              padding: '40px',
              color: '#333',
              position: 'relative'
            }}
          >

            <Row gutter={[32, 32]} align="middle">
              {/* Left side - Animation */}
              <Col xs={24} lg={10}>
                <div
                  style={{
                    borderRadius: 12,
                    overflow: 'hidden',
                    position: 'relative',
                    width: '100%',
                    paddingBottom: '56.25%', // 16:9 aspect ratio
                    height: 0,
                    background: '#000'
                  }}
                >
                  <iframe
                    src={`https://www.youtube.com/embed/${featuredGame.youtubeLink.split('v=')[1]}?autoplay=1&mute=1&loop=1&playlist=${featuredGame.youtubeLink.split('v=')[1]}`}
                    title={featuredGame.title}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />

                  {/* Booking Button Overlay */}
                  {showBookingButton && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 20,
                        left: 20,
                        right: 20,
                        textAlign: 'center',
                        zIndex: 10
                      }}
                    >
                      <Button
                        type="primary"
                        size="large"
                        icon={<CarOutlined />}
                        onClick={() => navigate('/')}
                        style={{
                          background: '#ff4d4f',
                          borderColor: '#ff4d4f',
                          color: 'white',
                          height: 48,
                          fontSize: 16,
                          boxShadow: '0 4px 12px rgba(255,77,79,0.4)',
                          animation: 'pulse 2s infinite'
                        }}
                      >
                        今すべ配車
                      </Button>
                    </div>
                  )}
                </div>
              </Col>

              {/* Right side - Selling points */}
              <Col xs={24} lg={14}>
                <div style={{ marginBottom: 16 }}>
                  <Badge.Ribbon text="大人気" color="red" style={{ fontSize: 14, position: 'absolute', top: -10, right: -10 }}>
                    <div></div>
                  </Badge.Ribbon>
                </div>

                <Space size="large" style={{ marginBottom: 16 }}>
                  <Statistic
                    title={<span style={{ color: '#666' }}>総プレイ回数</span>}
                    value="460万"
                    prefix={<EyeOutlined style={{ color: '#333' }} />}
                    valueStyle={{ color: '#333', fontSize: 24 }}
                  />
                </Space>

                <Title level={1} style={{ color: '#333', fontSize: 42, marginBottom: 16 }}>
                  Too many soccer balls
                </Title>

                <Text style={{ color: '#1890ff', fontSize: 24, display: 'block', marginBottom: 24, fontWeight: 'bold' }}>
                  メタバースで熱狂、現実で感動。ワンクリックで試合会場へ！
                </Text>

                <Text style={{ color: '#555', fontSize: 18, display: 'block', marginBottom: 24 }}>
                  今なら先着1,000名限定！観戦チケット付き移動パックを予約可能
                </Text>

                <Row gutter={16}>
                  <Col span={12}>
                    <Button
                      size="large"
                      onClick={() => navigate('/')}
                      style={{
                        background: '#52c41a',
                        borderColor: '#52c41a',
                        color: 'white',
                        height: 48,
                        fontSize: 16,
                        width: '100%'
                      }}
                    >
                      試合会場へ行く（予約する）
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      type="primary"
                      size="large"
                      icon={<PlayCircleOutlined />}
                      onClick={() => openLink(featuredGame.robloxLink)}
                      style={{
                        background: '#1890ff',
                        borderColor: '#1890ff',
                        color: 'white',
                        height: 48,
                        fontSize: 16,
                        width: '100%'
                      }}
                    >
                      今すぐプレイ
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </Card>

        {/* Other Games */}
        <div style={{ marginBottom: 32 }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
            多彩なテーマのメタバースを探索
          </Title>
        </div>

        <Row gutter={[24, 24]}>
          {otherGames.map((game, index) => (
            <Col xs={24} md={8} key={index}>
              <Card
                hoverable
                style={{
                  height: '100%',
                  borderRadius: 12,
                  overflow: 'hidden',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                }}
              >
                <div
                  style={{
                    background: game.imagePath
                      ? `linear-gradient(rgba(0,0,0,0.1), rgba(0,0,0,0.1)), url(${game.imagePath})`
                      : game.gradient,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    width: '100%',
                    height: 0,
                    paddingBottom: '56.25%', // 16:9 aspect ratio
                    marginBottom: 16,
                    position: 'relative',
                    borderRadius: '8px 8px 0 0',
                    overflow: 'hidden'
                  }}
                >
                  {/* Show emoji only if no image */}
                  {!game.imagePath && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: 32,
                        color: 'white',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
                      }}
                    >
                      {game.emoji || '🎮'}
                    </div>
                  )}
                </div>

                <div style={{ padding: '0 8px 16px' }}>
                  <Text style={{ fontSize: 18, color: '#1890ff', display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                    {game.category}
                  </Text>

                  <Title level={4} style={{ marginBottom: 8, fontSize: 18 }}>
                    {game.title}
                  </Title>

                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                    <CalendarOutlined /> {game.releaseDate}
                  </Text>

                  <Paragraph style={{ fontSize: 14, marginBottom: 16, minHeight: 42 }}>
                    {game.description}
                  </Paragraph>

                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Button
                      type="primary"
                      block
                      icon={<PlayCircleOutlined />}
                      onClick={() => openLink(game.robloxLink)}
                    >
                      プレイ
                    </Button>

                    <Button
                      block
                      onClick={() => navigate('/')}
                      style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                    >
                      {game.bookButtonText}
                    </Button>

                    <Button
                      block
                      onClick={() => openLink('https://docs.google.com/forms/d/e/1FAIpQLSfR0jDEhAtxHm-DlM4AlI0CpOP3c0otaEPTzzNExJOUMi3Nww/viewform')}
                      style={{ borderColor: '#52c41a', color: '#52c41a' }}
                    >
                      問い合わせ
                    </Button>
                  </Space>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Footer */}
        <Card style={{ marginTop: 48, textAlign: 'center', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', border: 'none' }}>
          <Title level={3} style={{ color: 'white', marginBottom: 16 }}>
            🎯 カスタムメタバース開発
          </Title>
          <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16, marginBottom: 24 }}>
            お客様のニーズに合わせたオリジナルメタバースをカスタマイズ制作いたします
          </Paragraph>
          <Button
            type="primary"
            size="large"
            onClick={() => navigate('/')}
            style={{
              background: 'rgba(255,255,255,0.2)',
              borderColor: 'rgba(255,255,255,0.4)',
            }}
          >
            ホームに戻る
          </Button>
        </Card>
        </div>
      </div>
    </>
  );
};

export default MetaversePage;